import { CruiseGuest, CruiseLine, CruiseSupplierCancelTerm, CruiseTermsRow } from './cruises.types';

/**
 * Cruises phase 4 - pure helpers of the Cruises tab: dates the agency reads and types as dd.mm.yyyy (C24, R-18 / R-56), ages and bands on
 * the sailing date (R-5), the occupancy the live price was asked for (R-3), money as the server gave it (D1 - nothing re-derived here).
 * QA doc: docs/features/cruises/qa.md R-1, R-3, R-5, R-18, R-50, R-56; R-116 / R-120 (a guest by age until the birth date is typed).
 */

const DDMMYYYY = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

const pad = (n: number): string => String(n).padStart(2, '0');

/** "07.11.2026" → "2026-11-07"; null for anything else, a non-existent day included (31.02.2026). */
export const parseDdMmYyyy = (text: string | null | undefined): string | null => {
  const m = DDMMYYYY.exec((text ?? '').trim());
  if (!m) return null;
  const [, d, mo, y] = m;
  const iso = `${y}-${mo}-${d}`;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso ? null : iso;
};

/** "2026-11-07" (or an ISO date-time) → "07.11.2026"; "—" for nothing. */
export const formatDdMmYyyy = (iso: string | null | undefined): string => {
  const m = ISO_DATE.exec(iso ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '—';
};

/** A UTC time of the API ("yyyy-MM-ddTHH:mm:ss") shown in the agent's own clock as dd.mm.yyyy HH:mm (C24). */
export const formatDateTimeUtc = (utc: string | null | undefined): string => {
  if (!utc) return '—';
  const withZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(utc) ? utc : `${utc}Z`;
  const d = new Date(withZone);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Full years on a day (both ISO dates). */
export const ageOn = (birthIso: string, onIso: string): number => {
  const [y, m, d] = birthIso.slice(0, 10).split('-').map(Number);
  const [Y, M, D] = onIso.slice(0, 10).split('-').map(Number);
  let age = Y - y;
  if (M < m || (M === m && D < d)) age--;
  return age;
};

/** Whole months of age on a day - an infant's age is told in months (R-5). */
export const monthsOn = (birthIso: string, onIso: string): number => {
  const [y, m, d] = birthIso.slice(0, 10).split('-').map(Number);
  const [Y, M, D] = onIso.slice(0, 10).split('-').map(Number);
  return (Y - y) * 12 + (M - m) - (D < d ? 1 : 0);
};

export type CruiseBand = 'A' | 'C' | 'J' | 'I';

/**
 * The age bands of the prototype (AGE_BANDS): Adult from 18, Child 13-17, Junior 2-12, Infant under 2. They only label a guest card and
 * the occupancy; the supplier's own age rules are applied by the server (the live price, R-5).
 */
/** The prototype's adult band starts at 18 (the age of majority, as the service's GuestChecks.AdultFrom). */
export const ADULT_FROM = 18;

const BANDS: { band: CruiseBand; from: number; label: string }[] = [
  { band: 'A', from: ADULT_FROM, label: 'Adult' },
  { band: 'C', from: 13, label: 'Teenager (13-17)' },
  { band: 'J', from: 2, label: 'Child (2-12)' },
  { band: 'I', from: 0, label: 'Infant (under 2)' }
];

export const bandOf = (age: number): CruiseBand => (BANDS.find((b) => age >= b.from) ?? BANDS[BANDS.length - 1]).band;

/** The translation key of a band's label. */
export const bandLabel = (band: CruiseBand): string => BANDS.find((b) => b.band === band)?.label ?? 'Adult';

/** R-5 (core/cruises-service#23): a birth date after the sailing date gives a negative age - no band, this label instead (never "Infant -3"). */
export const BORN_AFTER_SAILING = 'Born after the sailing date';

/** The translation key a guest's card / fare line shows for an age on the sailing date (R-5: a negative age is not a band). */
export const ageLabel = (age: number): string => (age < 0 ? BORN_AFTER_SAILING : bandLabel(bandOf(age)));

/** R-116 / R-120: a guest's age on the sailing date - from the typed birth date, else the age of the search picker; null = unknown. */
export const guestAge = (g: { birthDate: string | null; age?: number | null }, sailingDate: string): number | null =>
  g.birthDate ? ageOn(g.birthDate, sailingDate) : (g.age ?? null);

/** "2A1C" - the guests' bands on the sailing date (birth date, else the picker's age); "—" when no guest has either. */
export const occupancyOf = (
  guests: { birthDate: string | null; age?: number | null }[],
  sailingDate: string
): string => {
  const counts: Record<CruiseBand, number> = { A: 0, C: 0, J: 0, I: 0 };
  for (const g of guests) {
    const age = guestAge(g, sailingDate);
    if (age !== null && age >= 0) counts[bandOf(age)]++; // R-5: a guest born after the sailing is in no band (#23)
  }
  const text = (['A', 'C', 'J', 'I'] as CruiseBand[])
    .filter((b) => counts[b])
    .map((b) => `${counts[b]}${b}`)
    .join('');
  return text || '—';
};

/** What a live price was asked for: the offer, the guests' birth dates (else their picker ages, R-116) and the packages ticked. A different
 * signature = ask again (R-3). A guest is "seq:birthDate:age". */
export const priceSignature = (
  offerKey: string | null,
  guests: (Pick<CruiseGuest, 'seq' | 'birthDate'> & { age?: number | null })[],
  packages: { packageId: string; guestSeq: number }[]
): string =>
  [
    offerKey ?? '',
    guests.map((g) => `${g.seq}:${g.birthDate ?? ''}:${g.birthDate ? '' : (g.age ?? '')}`).join(','),
    packages
      .map((p) => `${p.packageId}:${p.guestSeq}`)
      .sort((a, b) => a.localeCompare(b))
      .join(',')
  ].join('|');

/** An amount as the server gave it, two decimals and the currency code ("551.25 EUR"). */
export const money = (value: number | null | undefined, currency: string | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : `${value.toFixed(2)} ${currency ?? ''}`.trim();

/** The Monday … Sunday key of a date (the itinerary's weekday). */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const weekdayKey = (iso: string): string => WEEKDAYS[new Date(`${iso.slice(0, 10)}T00:00:00Z`).getUTCDay()];

/** The first and last day of a month "yyyy-MM" as ISO dates. */
export const monthRange = (month: string): { from: string; to: string } => {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${pad(last)}` };
};

/** A month chip's label in the site's language ("декабрь 2026 г." / "December 2026"). */
export const monthLabel = (month: string, lang: string): string =>
  new Date(`${month}-01T00:00:00Z`).toLocaleString(lang.toLowerCase().startsWith('ru') ? 'ru-RU' : 'en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });

/** The translation key of a price line's label and its values (R-49: our wording, never the supplier's line code). */
export const lineLabel = (line: CruiseLine): { key: string; params: Record<string, string> } => {
  const n = line.guestSeq === null ? null : String(line.guestSeq);
  switch (line.kind) {
    case 'fare':
      return n ? { key: 'Cruise fare · guest {n}', params: { n } } : { key: 'Cruise fare · cabin', params: {} };
    case 'tax':
      return n
        ? { key: 'Port taxes and fees · guest {n}', params: { n } }
        : { key: 'Port taxes and fees · cabin', params: {} };
    case 'service_charge':
      return n ? { key: 'Service charge · guest {n}', params: { n } } : { key: 'Service charge · cabin', params: {} };
    case 'package':
      return n ? { key: 'Package · guest {n}', params: { n } } : { key: 'Package · cabin', params: {} };
    case 'discount':
      return { key: 'Discount', params: {} };
    default:
      return n ? { key: 'Other charges · guest {n}', params: { n } } : { key: 'Other charges', params: {} };
  }
};

/** A row of the cancellation terms: "0–30" or "91 and more", and "100 % of the price" / "150.00 EUR per guest". */
export const termsDays = (row: CruiseTermsRow): { key: string; params: Record<string, string> } =>
  row.daysTo === null
    ? { key: '{from} days and more', params: { from: String(row.daysFrom) } }
    : { key: '{from}–{to} days', params: { from: String(row.daysFrom), to: String(row.daysTo) } };

export const termsPenalty = (row: CruiseTermsRow): { key: string; params: Record<string, string> } => {
  if (row.kind === 'percent') return { key: '{value} % of the price', params: { value: String(row.value) } };
  const amount = money(row.value, row.currency);
  return row.unit === 'cabin'
    ? { key: '{amount} per cabin', params: { amount } }
    : { key: '{amount} per guest', params: { amount } };
};

/** R-110: a band of the cruise line's own schedule in dd.mm.yyyy (C24): "until", "from", both ends, or any time. */
export const supplierTermsPeriod = (row: CruiseSupplierCancelTerm): { key: string; params: Record<string, string> } => {
  if (row.from && row.to)
    return { key: '{from} - {to}', params: { from: formatDdMmYyyy(row.from), to: formatDdMmYyyy(row.to) } };
  if (row.to) return { key: 'until {date}', params: { date: formatDdMmYyyy(row.to) } };
  return row.from
    ? { key: 'from {date}', params: { date: formatDdMmYyyy(row.from) } }
    : { key: 'any time', params: {} };
};

/** The occupancy a price signature was asked for ("2A1C" of "key|1:1980-05-01:,2::18,3::7|") - the reprice banner's "before" (R-3). */
export const occupancyOfSignature = (signature: string | null, sailingDate: string): string => {
  const guests = (signature?.split('|')[1] ?? '')
    .split(',')
    .filter((g) => g)
    .map((g) => {
      const [, birthDate, age] = g.split(':');
      return { birthDate: birthDate || null, age: age ? Number(age) : null };
    });
  return occupancyOf(guests, sailingDate);
};

/** R-54: the number (1-based, as the cards show it) of the first guest whose gender is not chosen, or null when every guest has one. */
export const guestWithoutGender = (guests: Pick<CruiseGuest, 'isMale'>[]): number | null => {
  const i = guests.findIndex((g) => g.isMale === null || g.isMale === undefined);
  return i < 0 ? null : i + 1;
};

/** Today in the agent's own calendar as an ISO date ("2026-10-06"). */
export const todayIso = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/** R-113: what is wrong with a typed passport issue date - later than today, or not before the passport's expiry; null = nothing (or not typed). */
export const passportIssueProblem = (
  g: Pick<CruiseGuest, 'passportIssued' | 'passportExpires'>,
  today: string
): 'PASSPORT_ISSUE_DATE_IS_INCORRECT' | 'PASSPORT_ISSUE_DATE_AFTER_EXPIRY' | null => {
  if (!g.passportIssued) return null;
  if (g.passportIssued > today) return 'PASSPORT_ISSUE_DATE_IS_INCORRECT';
  return g.passportExpires && g.passportIssued >= g.passportExpires ? 'PASSPORT_ISSUE_DATE_AFTER_EXPIRY' : null;
};

/** The first guest (1-based number) whose passport issue date the site refuses, with the code; null = none. */
export const guestWithPassportIssueProblem = (
  guests: Pick<CruiseGuest, 'passportIssued' | 'passportExpires'>[],
  today: string
): { n: number; code: string } | null => {
  for (let i = 0; i < guests.length; i++) {
    const code = passportIssueProblem(guests[i], today);
    if (code) return { n: i + 1, code };
  }
  return null;
};
